import { useState, useRef } from "react";
import { Badge } from "../components/ui/badge";
import { UploadSimple, Spinner, CheckCircle, Buildings, ArrowRight, CurrencyInr } from "@phosphor-icons/react";
import { nmcUploadSeatMatrix, feesUploadCsv } from "../lib/api";

export default function NmcIngestion({ onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await nmcUploadSeatMatrix(formData);
      setUploadResult(res.data);
      if (onUploadComplete) onUploadComplete(res.data.batch_id);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
    setUploading(false);
  };

  const reset = () => {
    setUploadResult(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div data-testid="nmc-ingestion">
      <div className="border border-zinc-200 border-t-0 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Buildings size={18} weight="bold" className="text-[#6043EF]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">NMC APPROVED SEAT MATRIX</span>
        </div>
        <p className="text-sm text-[#52525B] font-sans mb-4">
          Upload the NMC-published seat matrix PDF (MBBS/BDS). Colleges will be matched against the existing database
          and saved for review in the <strong>REVIEW COLLEGES</strong> tab.
        </p>

        {!uploadResult && (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                onChange={handleUpload}
                className="block w-full text-sm text-[#52525B] font-sans
                  file:mr-3 file:py-2 file:px-4 file:border-0 file:text-xs file:font-bold
                  file:uppercase file:tracking-wider file:bg-[#6043EF] file:text-white
                  file:cursor-pointer file:transition-colors file:hover:bg-[#4A28C2]"
                data-testid="nmc-file-input"
              />
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-xs text-[#6043EF] font-bold">
                <Spinner size={14} className="animate-spin" /> PARSING & MATCHING...
              </div>
            )}
          </div>
        )}

        {uploadResult && (
          <div className="animate-fade-in" data-testid="nmc-upload-success">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={16} weight="bold" className="text-[#00C04B]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C04B]">
                PARSED & MATCHED — SAVED TO DATABASE
              </span>
            </div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="font-['IBM_Plex_Mono'] text-sm text-[#09090B] font-bold">{uploadResult.total} colleges</span>
              <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">{uploadResult.course} — AY {uploadResult.current_year}</span>
              <Badge className="bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B] text-[10px]">{uploadResult.auto_matched} AUTO</Badge>
              <Badge className="bg-[#FFC107]/10 text-[#92700C] border-[#FFC107] text-[10px]">{uploadResult.review_needed} REVIEW</Badge>
              <Badge className="bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00] text-[10px]">{uploadResult.unmatched} NEW</Badge>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 p-3 border border-[#6043EF] bg-[#6043EF]/5 text-sm text-[#6043EF] font-sans">
                <ArrowRight size={14} weight="bold" />
                Go to the <strong>REVIEW COLLEGES</strong> tab to review and approve matches.
              </div>
              <button
                onClick={reset}
                className="h-8 px-4 border border-zinc-300 text-[#52525B] text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-50"
                data-testid="nmc-upload-another-btn"
              >
                UPLOAD ANOTHER
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-3 p-3 border border-[#FF2A00] bg-[#FF2A00]/5 text-sm text-[#FF2A00]">{error}</div>
        )}
      </div>

      {/* Fees CSV Upload */}
      <FeesUpload />
    </div>
  );
}

function FeesUpload() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await feesUploadCsv(formData);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="border border-zinc-200 border-t-0 p-6" data-testid="fees-upload">
      <div className="flex items-center gap-3 mb-4">
        <CurrencyInr size={18} weight="bold" className="text-[#00C04B]" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">FEES CSV UPLOAD</span>
      </div>
      <p className="text-sm text-[#52525B] font-sans mb-4">
        Upload college fees CSV (govt/regular/NRI fees, intake, donation). Colleges will be matched and saved for review in the <strong>REVIEW COLLEGES</strong> tab.
      </p>

      {!result && (
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleUpload}
              className="block w-full text-sm text-[#52525B] font-sans
                file:mr-3 file:py-2 file:px-4 file:border-0 file:text-xs file:font-bold
                file:uppercase file:tracking-wider file:bg-[#00C04B] file:text-white
                file:cursor-pointer file:transition-colors file:hover:bg-[#009940]"
              data-testid="fees-file-input"
            />
          </div>
          {uploading && (
            <div className="flex items-center gap-2 text-xs text-[#00C04B] font-bold">
              <Spinner size={14} className="animate-spin" /> PARSING & MATCHING...
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="animate-fade-in" data-testid="fees-upload-success">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={16} weight="bold" className="text-[#00C04B]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C04B]">PARSED & MATCHED — SAVED TO DATABASE</span>
          </div>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="font-['IBM_Plex_Mono'] text-sm text-[#09090B] font-bold">{result.total} colleges</span>
            <Badge className="bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B] text-[10px]">{result.auto_matched} AUTO</Badge>
            <Badge className="bg-[#FFC107]/10 text-[#92700C] border-[#FFC107] text-[10px]">{result.review_needed} REVIEW</Badge>
            <Badge className="bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00] text-[10px]">{result.unmatched} NEW</Badge>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 p-3 border border-[#00C04B] bg-[#00C04B]/5 text-sm text-[#00C04B] font-sans">
              <ArrowRight size={14} weight="bold" />
              Go to the <strong>REVIEW COLLEGES</strong> tab to review and approve matches.
            </div>
            <button
              onClick={() => { setResult(null); if (fileRef.current) fileRef.current.value = ""; }}
              className="h-8 px-4 border border-zinc-300 text-[#52525B] text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-50"
            >
              UPLOAD ANOTHER
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 p-3 border border-[#FF2A00] bg-[#FF2A00]/5 text-sm text-[#FF2A00]">{error}</div>
      )}
    </div>
  );
}
